import type { UseCurriculum_EstimatedAcademicTermFragment as EstimatedAcademicTerm } from 'bundles/degree-home/components/v3-dpm/lib/__queries__/__generated__/dpmSharedFragments';
import type { DegreePlan_TermPlanFragment as TermPlan } from 'bundles/degree-home/components/v3-dpm/lib/__queries__/__generated__/learnerPlanFragment';
import type {
  UseCurriculum_DegreeCurriculumFragment as DpDegreeCurriculum,
  UseCurriculum_PbaCurriculumFragment as DpPbaCurriculum,
} from 'bundles/degree-home/components/v3-dpm/lib/__queries__/__generated__/useCurriculum';
import {
  getCreditsSummaryForSingleSegment,
  getMedianCreditValue,
} from 'bundles/degree-home/components/v3-dpm/lib/gradeUtils';
import { findLastIndex, now } from 'bundles/degree-home/components/v3-dpm/lib/otherUtils';
import type { ProgressionCalculatorResult } from 'bundles/degree-home/components/v3-dpm/types/gradeTypes';
import type { EstimatedGradTermSummary } from 'bundles/degree-home/components/v3-dpm/types/graduationTypes';

const BALL_STATE_ID = '1452';
const ILLINOIS_ID = '1451';
const NORTHEASTERN_ID = '517';

interface RealTerm {
  name: string;
  endDate: number;
}

interface FutureTerm {
  name: string;
}

export type Term = EstimatedAcademicTerm | TermPlan;

export type GraduationTermStoreEntry = RealTerm | FutureTerm;

const isRealTerm = (term: GraduationTermStoreEntry): term is RealTerm => (term as RealTerm)?.endDate !== undefined;
const currentTime = new Date(now()).getTime();
/** A hardcoded collection of terms for our partners to power graduation date calculation */
export const GRADUATION_TERM_STORE: Record<string, GraduationTermStoreEntry[]> = {
  [BALL_STATE_ID]: [
    {
      name: 'Fall 2023',
      endDate: 1702598400000,
    },
    {
      name: 'Spring 2024',
      endDate: 1714694400000,
    },
    {
      name: 'Summer 2024',
      endDate: 1721347200000,
    },
    { name: 'Fall 2024' },
    { name: 'Spring 2025' },
    { name: 'Summer 2025' },
    { name: 'Fall 2025' },
    { name: 'Spring 2026' },
    { name: 'Summer 2026' },
    { name: 'Fall 2026' },
    { name: 'Spring 2027' },
    { name: 'Summer 2027' },
    { name: 'Fall 2027' },
    { name: 'Spring 2028' },
    { name: 'Summer 2028' },
    { name: 'Fall 2028' },
    { name: 'Spring 2029' },
    { name: 'Summer 2029' },
    { name: 'Fall 2029' },
    { name: 'Spring 2030' },
    { name: 'Summer 2030' },
    { name: 'Fall 2030' },
  ],
  [ILLINOIS_ID]: [
    {
      name: 'Coursera Fall A Term',
      endDate: 1697241600000,
    },
    {
      name: 'Coursera Fall B Term',
      endDate: 1702080000000,
    },
    {
      name: 'Coursera A term',
      endDate: 1709856000000,
    },
    {
      name: 'Coursera B Term',
      endDate: 1715299200000,
    },
    {
      name: 'Summer 2024 A',
      endDate: 1718928000000,
    },
    {
      name: 'Summer 2024 B',
      endDate: 1722556800000,
    },
    {
      name: 'Fall 2024 A',
      endDate: 1728691200000,
    },
    {
      name: 'Fall 2024 B',
      endDate: 1733529600000,
    },
    {
      name: 'Spring 2025 A',
      endDate: 1740787200000,
    },
    {
      name: 'Spring 2025 B',
      endDate: 1746835200000,
    },
    {
      name: 'Summer 2025 A',
      endDate: 1750464000000,
    },
    {
      name: 'Summer 2025 B',
      endDate: 1754092800000,
    },
    { name: 'Fall 2025 A' },
    { name: 'Fall 2025 B' },
    { name: 'Spring 2026 A' },
    { name: 'Spring 2026 B' },
    { name: 'Summer 2026 A' },
    { name: 'Summer 2026 B' },
    { name: 'Fall 2026 A' },
    { name: 'Fall 2026 B' },
  ],
  [NORTHEASTERN_ID]: [
    { name: 'Spring Semester Full Term Grad' },
    { name: 'Summer Full Semester' },
    { name: 'Fall Semester Full Term Grad' },
  ],
};

/**
 * Determine the position of the learner in the collection of terms
 * @param termsSortedInAscDateOrder the term store entries
 * @param degreeIdentifierForErrorLogging identifier for error logging
 * @private
 * @returns the index of the term store
 */
export const _getCurrentTermIndex = (
  termsSortedInAscDateOrder: GraduationTermStoreEntry[],
  degreeIdentifierForErrorLogging: string
): number => {
  let currentTermIndex: number | null = null;

  for (let index = 0; index < termsSortedInAscDateOrder.length; index += 1) {
    const term = termsSortedInAscDateOrder[index];
    // @ts-expect-error TS2345 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
    if (isRealTerm(term) && currentTime < term.endDate) {
      currentTermIndex = index;
      break;
    }
  }

  if (currentTermIndex === null) {
    throw new Error(`Current time exceeds known terms for degree ${degreeIdentifierForErrorLogging}`);
  }

  return currentTermIndex;
};

interface CurriculumCompletionData {
  medianCredit: number;
  remainingCredits: number;
}
/**
 * Pick completion data from the curriculum
 * @private
 * @returns the curriculum completion data or null in the case of a non-error edge state where we shouldn't show the component
 */
export const _getCurriculumCompletionData = (
  curriculum: DpDegreeCurriculum | DpPbaCurriculum,
  degreeIdentifierForErrorLogging: string
): CurriculumCompletionData | null => {
  const { minCredits } = curriculum;
  let completedCreditsTotal = 0;

  curriculum.categories.forEach((category) => {
    const minPassingGradeForCategory = category.minPassingGradeForCourses ?? curriculum.minPassingGradeForCourses;
    const segments = category.segments;
    segments.forEach((segment) => {
      const segmentCreditsSummary = getCreditsSummaryForSingleSegment(minPassingGradeForCategory, segment);
      completedCreditsTotal += segmentCreditsSummary.completed;
    });
  });

  const remainingCredits = minCredits - completedCreditsTotal;

  // if remaining credits is a negative number i.e. learner has completed more credits than the minimum required credits return null
  if (remainingCredits < 0) return null;

  const allCourses = curriculum.categories.flatMap((category) =>
    category.segments.flatMap((segment) => segment.courses)
  );
  const medianCredit = getMedianCreditValue(allCourses);

  // a null or 0 median credit is a result of an empty course list
  if (!medianCredit) {
    throw new Error(`Could not determine median credit for degree ${degreeIdentifierForErrorLogging}`);
  }

  return {
    medianCredit,
    remainingCredits,
  };
};

interface CalculateEstimatedGradDateArgs {
  termStore: GraduationTermStoreEntry[];
  degreeIdentifierForErrorLogging: string;
  remainingCredits: number;
  medianCredit: number;
  currentTermIndex: number;
}

/**
 * An internal calculation function given all data
 * @private
 * @returns 3-tuple of term names, aligned to 1, 2, and 3 courses completed per term
 */
export const _calculateEstimatedGradDate = ({
  termStore,
  degreeIdentifierForErrorLogging,
  remainingCredits,
  medianCredit,
  currentTermIndex,
}: CalculateEstimatedGradDateArgs): [string, string, string] => {
  const numberOfTermsOneCourse = Math.ceil(remainingCredits / medianCredit);
  const numberOfTermsTwoCourses = Math.ceil(remainingCredits / (medianCredit * 2));
  const numberOfTermsThreeCourses = Math.ceil(remainingCredits / (medianCredit * 3));

  const onePerTerm = termStore[currentTermIndex + numberOfTermsOneCourse];
  const twoPerTerm = termStore[currentTermIndex + numberOfTermsTwoCourses];
  const threePerTerm = termStore[currentTermIndex + numberOfTermsThreeCourses];

  if (onePerTerm === undefined || twoPerTerm === undefined || threePerTerm === undefined) {
    throw new Error(`Estimated graduation date exceeds known terms for degree ${degreeIdentifierForErrorLogging}`);
  }

  return [onePerTerm.name, twoPerTerm.name, threePerTerm.name];
};

export interface EstimatedGraduationDateResult {
  /** The median credit for the curriculum */
  medianCredit: number;
  /** 3-tuple of term names, aligned to 1, 2, and 3 courses completed per term */
  graduationDateEstimates: [string, string, string];
}

/**
 * Calculate the estimated graduation dates for learner progress 1/2/3 courses per term
 */
export const getEstimatedGraduationDate = (
  curriculum: DpDegreeCurriculum | DpPbaCurriculum,
  partnerId: number,
  degreeIdentifierForErrorLogging: string
): EstimatedGraduationDateResult | null => {
  const termStore = GRADUATION_TERM_STORE[`${partnerId}`];
  if (!termStore) {
    throw new Error(`Unsupported partner id ${partnerId} for estimated graduation date`);
  }
  const currentTermIndex = _getCurrentTermIndex(termStore, degreeIdentifierForErrorLogging);

  const curriculumCompletionData = _getCurriculumCompletionData(curriculum, degreeIdentifierForErrorLogging);
  if (!curriculumCompletionData) {
    return null;
  }

  const { medianCredit, remainingCredits } = curriculumCompletionData;
  const graduationDateEstimates = _calculateEstimatedGradDate({
    currentTermIndex,
    termStore,
    degreeIdentifierForErrorLogging,
    remainingCredits,
    medianCredit,
  });

  return {
    medianCredit,
    graduationDateEstimates,
  };
};

/**
 * Extract the credits summary of a degree curriculum from the overall progression
 * @param degreeCurriculum the degree curriculum
 * @param overallProgression the progression
 * @returns the number of completed, in-progress, and planned credits
 */
export const getCurriculumCreditInfo = (
  degreeCurriculum: DpDegreeCurriculum,
  overallProgression: ProgressionCalculatorResult
): {
  completed: number | undefined;
  inProgress: number | undefined;
  planned: number | undefined;
} => {
  const curriculumProgression = overallProgression.curriculums[degreeCurriculum.id];
  const completed: number | undefined = curriculumProgression?.summary?.completed;
  const inProgress: number | undefined = curriculumProgression?.summary?.inProgress;
  const planned: number | undefined = curriculumProgression?.summary?.planned;
  return { completed, inProgress, planned };
};

/**
 * Calculate the remaining credits
 * @param requiredCredits the total number of required credits
 * @param completed the completed credits
 * @param inProgress the in-progress credits
 * @param planned the planned credits
 * @returns the remaining credits
 */
export const getRemainingCredits = (
  requiredCredits: number,
  completed: number,
  inProgress: number,
  planned: number
): number => requiredCredits - completed - inProgress - planned;

/**
 * Check if a term has planned or in-progress courses
 * @param termPlan The term to check
 * @returns {boolean} - True if the term has planned or in-progress courses, false otherwise
 */
export const hasPlannedOrInProgressCourses = (termPlan: TermPlan): boolean => {
  return (
    (termPlan.plannedSegmentCourses ?? []).filter(
      (course) =>
        course.personalizedSegmentCourse?.learnerProgress?.state === 'PLANNED' ||
        course.personalizedSegmentCourse?.learnerProgress?.state === 'IN_PROGRESS'
    ).length > 0
  );
};

/**
 * Check if a term is current or in the future
 * @param term The term to check
 * @returns {boolean} - True if the term is current or in the future, false otherwise
 */
export const isCurrentOrFutureTerm = (term: Term): boolean => {
  return !term.linkedTerm || (term.linkedTerm && new Date(term.linkedTerm.endDate).getTime() >= currentTime);
};

/**
 * Find the last planned or in-progress term in a learner plan
 * @param termPlans the term plans of the learner
 * @returns the index of the planned or in-progress term
 */
export const findAnchorTermInTermPlan = (termPlans: TermPlan[]): number => {
  return findLastIndex(
    termPlans,
    (termPlan) => isCurrentOrFutureTerm(termPlan) && hasPlannedOrInProgressCourses(termPlan)
  );
};

/**
 * Helper function to check if a term is in progress
 * @param {EstimatedAcademicTerm} term - The term to check
 * @returns {boolean} - True if the term is in progress, false otherwise
 */
export const isTermInProgress = (term: Term): boolean => {
  if (!term.linkedTerm) {
    return false;
  }

  return (
    new Date(term.linkedTerm.startDate).getTime() <= currentTime &&
    new Date(term.linkedTerm.endDate).getTime() >= currentTime
  );
};

/**
 * Helper function to get the index of the in-progress term with the farthest end date
 * @param {Term[]} terms - The list of terms to search through
 * @param {number} startingIndex - The index to start the search from
 * @returns {number} - The index of the in-progress term with the farthest end date, or -1 if not found
 */
export const getLatestInProgressTermIndex = (terms: Term[], startingIndex: number): number => {
  const { latestTermIndex } = terms.slice(startingIndex).reduce(
    (acc, term, i) => {
      if (isTermInProgress(term) && new Date(term.linkedTerm!.endDate).getTime() > acc.latestEndDate) {
        return {
          latestEndDate: new Date(term.linkedTerm!.endDate).getTime(),
          latestTermIndex: i + startingIndex,
        };
      }
      return acc;
    },
    { latestEndDate: 0, latestTermIndex: -1 }
  );

  return latestTermIndex;
};

/**
 * Helper function to get the index of the first future term in AcademicTermsPaginated
 * @param {EstimatedAcademicTerm[]} terms - The list of terms to search through
 * @returns {number} - The index of the first future term, or -1 if not found
 */
export const getFirstFutureTermIndex = (terms: EstimatedAcademicTerm[]): number => {
  const firstFutureTermIndex = terms.findIndex((term) => {
    const futureTerm = term.linkedTerm
      ? new Date(term.linkedTerm.startDate).getTime() >= currentTime
      : !term.linkedTerm;

    return futureTerm;
  });

  return firstFutureTermIndex;
};

/**
 * Helper function to find the index of the next available term in the estimated terms
 * @param {Term[]} terms - The list of terms to search through
 * @returns {number} - The index of the in-progress term with the farthest end date, or -1 if not found
 */
export const findNextAvailableTermIndex = (terms: Term[]): number => {
  // Find the first in-progress or future term
  const firstInProgressOrFutureTermIndex = terms.findIndex((term) => isCurrentOrFutureTerm(term));

  // @ts-expect-error TS2345 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
  if (firstInProgressOrFutureTermIndex !== -1 && isTermInProgress(terms[firstInProgressOrFutureTermIndex])) {
    // If the first term found is an in-progress term, find the in-progress term with the farthest end date
    return getLatestInProgressTermIndex(terms, firstInProgressOrFutureTermIndex);
  }

  return firstInProgressOrFutureTermIndex;
};

/**
 * Helper function to find the index of the closest term to the current time
 * @param {EstimatedAcademicTerm[]} terms - The list of terms to search through
 * @param {string} degreeIdentifierForErrorLogging - The identifier of the degree
 * @returns {number} - The index of the in-progress term with the farthest end date, or the first future term
 */
export const findClosestTermIndex = (
  terms: EstimatedAcademicTerm[],
  degreeIdentifierForErrorLogging: string
): number => {
  const currentTermIndex = getLatestInProgressTermIndex(terms, 0);

  if (currentTermIndex >= 0) {
    return currentTermIndex;
  }

  const firstFutureTermIndex = getFirstFutureTermIndex(terms);

  if (firstFutureTermIndex >= 0) {
    return firstFutureTermIndex;
  }

  throw new Error(`Current time exceeds known terms for degree ${degreeIdentifierForErrorLogging}`);
};

/**
 * Helper function to find the index of a term plan in the AcademicTermsPaginated
 * @param {EstimatedAcademicTerm[]} terms - The list of terms to search through
 * @param {TermPlan} termPlan - The term plan to search for
 * @param {string} degreeIdentifierForErrorLogging - The identifier of the degree
 * @returns {number} - The index of the term plan in the AcademicTermsPaginated list
 */
export const getTermIndexInPaginatedTerms = (
  terms: EstimatedAcademicTerm[],
  termPlan: TermPlan,
  degreeIdentifierForErrorLogging: string
): number => {
  const termIndexInPaginatedTerms = terms.findIndex((term) => term.estimatedTermId === termPlan.estimatedTermId);

  if (termIndexInPaginatedTerms === -1) {
    throw new Error(
      `Term plan id ${termPlan.estimatedTermId} not found in paginatedAcademicTerms for degree ${degreeIdentifierForErrorLogging}`
    );
  }

  return termIndexInPaginatedTerms;
};

/**
 * Get completion data for a degree curriculum
 * @param {DpDegreeCurriculum} degreeCurriculum - The degree curriculum
 * @param {ProgressionCalculatorResult} overallProgression - The overall progression
 * @param {string} degreeId - The id of the degree
 * @returns {remainingCredits: number, medianCredit: number} - The remaining credits and the median credit
 */
export const getCurriculumCompletionData = (
  degreeCurriculum: DpDegreeCurriculum,
  overallProgression: ProgressionCalculatorResult,
  degreeId: string
) => {
  const { completed = 0, inProgress = 0, planned = 0 } = getCurriculumCreditInfo(degreeCurriculum, overallProgression);

  let remaining = getRemainingCredits(degreeCurriculum.minCredits, completed, inProgress, planned);
  remaining = remaining < 0 ? 0 : remaining;

  const allCourses = degreeCurriculum.categories.flatMap((category) =>
    category.segments.flatMap((segment) => segment.courses)
  );

  const medianCredit = getMedianCreditValue(allCourses);
  if (!medianCredit) {
    throw new Error(`Could not determine median credit for degree ${degreeId}`);
  }

  return { remainingCredits: remaining, medianCredit };
};

/**
 * Get the name of the estimated graduation term
 * @param {EstimatedAcademicTerm[]} terms - The list of all available terms
 * @param {number | null} additionalTermsNecessary the number of additional terms outside of the learner plan that the learner should complete to graduate
 * @returns {string | null} - The name of the term, or null if not found
 */
export const getEstimatedGraduationTermName = (
  terms: EstimatedAcademicTerm[],
  anchorTermIndex: number,
  additionalTermsNecessary: number,
  degreeIdentifierForErrorLogging: string
): string => {
  const estimatedGraduationIndex = anchorTermIndex + additionalTermsNecessary;
  const estimatedGraduationTerm = terms[estimatedGraduationIndex];

  if (estimatedGraduationTerm === undefined) {
    throw new Error(`Estimated graduation date exceeds known terms for degree ${degreeIdentifierForErrorLogging}`);
  }

  return estimatedGraduationTerm.estimatedTermName;
};

/**
 * Calculate the number of terms that the learner still needs to take courses in before graduation, based on the current progress and the learner's plan
 * @param degreeCurriculum the degree curriculum
 * @param overallProgression the overall progression of the learner
 * @param termPlans the term plans of the learner
 * @param terms the list of academicTermsPaginated
 * @returns an object that has the data of number of terms and the median credit used for the calculation (if applicable)
 */
export const getEstimatedGraduationTermSummary = (
  degreeCurriculum: DpDegreeCurriculum,
  overallProgression: ProgressionCalculatorResult,
  termPlans: TermPlan[],
  terms: EstimatedAcademicTerm[]
): EstimatedGradTermSummary | null => {
  const hasNonEmptyTermPlans = termPlans.length > 0;
  const degreeId = degreeCurriculum.id;

  const { remainingCredits, medianCredit } = getCurriculumCompletionData(
    degreeCurriculum,
    overallProgression,
    degreeId
  );

  // we assume that the learner takes 2 courses per term
  const creditNumPerTerm = medianCredit * 2;
  const additionalTermsNecessary = Math.ceil(remainingCredits / creditNumPerTerm);

  // the term plan is considered complete if there are no remaining credits and the term plan is not empty
  const hasCompleteGradPlan = remainingCredits === 0 && hasNonEmptyTermPlans;

  const closestTermToCurrentTimeIndexInPaginatedTerms = findClosestTermIndex(terms, degreeId);

  // Case 1: DegreePlanIsComplete the estimated graduation date is the last planned term
  if (hasCompleteGradPlan) {
    const lastTermIndex = termPlans.length - 1;
    const lastPlannedTerm = termPlans[lastTermIndex];
    // @ts-expect-error TS18048 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
    const lastTermName = lastPlannedTerm.estimatedTermName;
    // @ts-expect-error TS2345 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
    const lastPlannedTermIndexInPaginatedTerms = getTermIndexInPaginatedTerms(terms, lastPlannedTerm, degreeId);

    return {
      termNum: lastPlannedTermIndexInPaginatedTerms - closestTermToCurrentTimeIndexInPaginatedTerms,
      creditNumPerTerm: null,
      termName: lastTermName,
    };
  }

  const lastPlannedTermIndex = findAnchorTermInTermPlan(termPlans);

  if (lastPlannedTermIndex !== -1) {
    // Case 2: Term plan is incomplete and has in-progress/future terms with plannedSegmentCourses
    const lastPlannedTerm = termPlans[lastPlannedTermIndex];

    // @ts-expect-error TS2345 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
    const lastPlannedTermIndexInPaginatedTerms = getTermIndexInPaginatedTerms(terms, lastPlannedTerm, degreeId);
    const termsBetweenNowAndLastPlannedTerm =
      lastPlannedTermIndexInPaginatedTerms - closestTermToCurrentTimeIndexInPaginatedTerms;

    const estimatedGraduationTermName = getEstimatedGraduationTermName(
      terms,
      lastPlannedTermIndexInPaginatedTerms,
      additionalTermsNecessary,
      degreeId
    );

    return {
      termNum: termsBetweenNowAndLastPlannedTerm + additionalTermsNecessary,
      creditNumPerTerm,
      termName: estimatedGraduationTermName,
    };
  } else {
    // Case 3: Term plan is incomplete and is empty / only has past terms
    const estimatedGraduationTermName = getEstimatedGraduationTermName(
      terms,
      closestTermToCurrentTimeIndexInPaginatedTerms,
      additionalTermsNecessary,
      degreeId
    );

    return {
      termNum: additionalTermsNecessary,
      creditNumPerTerm,
      termName: estimatedGraduationTermName,
    };
  }
};
